import { countBy } from 'lodash';
import {
  CreateSourceCommissionDocumentsRequest,
  PaymentMethod,
  SourceCommission,
  SourceCommissionItemInput,
  SourcePaymentType
} from './type/sourceCommissions';

export const SOURCE_COMMISSION_ERRORS: Record<number, string> = {
  402: 'Přihlášený uživatel není obchodník.',
  440: 'Doklad se nepodařilo smazat.',
  453: 'Nebyla vybrána žádná platná POJ nebo je některý řádek neplatný. POJ už mohla být vyplacena.',
  454: 'Doklad nebyl nalezen.',
  455: 'Není nastavená číselná řada dokladů. Kontaktujte administrátora, musí to vyřešit administrátor.',
  456: 'Doklad se nepodařilo uložit.',
  457: 'Překročen limit dokladu: maximálně 5 POJ, při platbě v hotovosti maximálně 10 000 Kč.'
};

const DEFAULT_ERROR = 'Došlo k chybě. Zkuste to prosím znovu.';

export const getErrorMessage = (code?: number) => (code && SOURCE_COMMISSION_ERRORS[code]) || DEFAULT_ERROR;

/** Kód chyby: z těla odpovědi (`statusCode`), jinak z HTTP statusu. Nikdy z textu zprávy. */
export const getErrorCode = (error: unknown): number | undefined => {
  const e = error as
    | { statusCode?: number; response?: { status?: number; data?: { statusCode?: number } } }
    | undefined;
  return e?.response?.data?.statusCode ?? e?.statusCode ?? e?.response?.status;
};

export const MAX_DOCUMENT_ITEMS = 5;
export const CASH_DOCUMENT_LIMIT = 10000;
export const FIRST_ACCOUNTING_YEAR = 2025;

/** Odhad: 1 doklad na tipaře, u více než 5 POJ dalších. Hotovostní strop zná jen BE, rozhoduje odpověď BE. */
export const estimateDocuments = (rows: Pick<SourceCommission, 'sourceId'>[]) =>
  Object.values(countBy(rows, 'sourceId')).reduce((sum, count) => sum + Math.ceil(count / MAX_DOCUMENT_ITEMS), 0);

export const getPaymentTypeLabel = (paymentType?: SourcePaymentType) =>
  paymentType === 'cash' ? 'Hotově' : paymentType === 'online' ? 'Převodem' : undefined;

export const getAccountingYears = (currentYear: number) => {
  const last = Math.max(currentYear, FIRST_ACCOUNTING_YEAR);
  return Array.from({ length: last - FIRST_ACCOUNTING_YEAR + 1 }, (_, i) => last - i);
};

/** Důvod, proč nejde doklad uložit kvůli limitům BE (jinak undefined). */
export const getEditLimitIssue = (items: { amount: number }[], paymentMethod?: PaymentMethod): string | undefined => {
  if (items.length > MAX_DOCUMENT_ITEMS) return `Doklad může mít nejvýše ${MAX_DOCUMENT_ITEMS} POJ.`;
  if (paymentMethod === 'cash' && sumAmounts(items) > CASH_DOCUMENT_LIMIT)
    return 'Doklad se platí v hotovosti, součet nesmí přesáhnout 10 000 Kč.';
  return undefined;
};

export const sumAmounts = (rows: { amount?: number }[]) =>
  rows.reduce((sum, { amount }) => (typeof amount === 'number' && Number.isFinite(amount) ? sum + amount : sum), 0);

export const isValidAmount = (amount?: number) => typeof amount === 'number' && Number.isFinite(amount) && amount > 0;

/** `amount` se posílá jen u změněné částky, jinak BE použije vypočtenou POJ. */
export const buildCreateBody = (
  rows: SourceCommission[],
  overrides: Record<number, number | undefined>,
  issueDate: string
): CreateSourceCommissionDocumentsRequest => ({
  issueDate,
  items: rows.map(({ accidentId, amount }): SourceCommissionItemInput => {
    const override = overrides[accidentId];
    return override !== undefined && override !== amount ? { accidentId, amount: override } : { accidentId };
  })
});

export const buildCancelBody = (rows: Pick<SourceCommission, 'accidentId'>[]) => ({
  items: rows.map(({ accidentId }) => ({ accidentId }))
});

/** Klíč výběru + částek: změna zneplatní předchozí náhled. */
export const getSelectionSignature = (rows: { accidentId: number; amount: number }[], issueDate: string) =>
  `${issueDate}|${rows
    .map(({ accidentId, amount }) => `${accidentId}:${amount}`)
    .sort()
    .join(',')}`;

export const buildUpdateBody = (items: { accidentId: number; amount: number }[]) => ({
  items: items.map(({ accidentId, amount }) => ({ accidentId, amount }))
});

export const withCacheBuster = (url: string | null | undefined, version: number = Date.now()) =>
  url ? `${url}${url.includes('?') ? '&' : '?'}v=${version}` : null;
